import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useEffect, useState, useMemo, useContext } from "react";

import { initialValues, validationSchema, getSchemas } from "./floodedRice";
import { useAppSelector, useThreadsProcessor } from "@app/hooks";
import { selectCurrentProject } from "@app/features/project/projectSlice";
import {
	useContextAwareGetFloodedRice,
	useContextAwareGetFloodedRiceDefaults,
	useContextAwareGetFloodedRiceMinorSeasons,
	useUpdateFloodedRiceMutation,
	useUpdateFloodedRiceMinorSeasonMutation,
} from "@hooks/index";
import type { ModuleFull } from "./floodedRiceTypes";
import useModuleHook from "../useModuleHook";
import FloodedRiceTierOne from "./FloodedRiceTierOne";
import useErrorMessage from "@utils/useErrorMessage";
import ModuleContext from "../../ModuleContext";
import GenericTierTwo from "../GenericTierTwo";
import { BEModulesClassNames } from "@utils/moduleList";
import { useGetCropYieldDefaultQuery, useGetCultivationDaysDefaultQuery } from "@app/features/other/genericApiSlice";
import { BEModules } from "@utils/beModules";
import { FEModules } from "@utils/moduleList";
import { selectCurrentBuilder } from "@app/features/builder/builderSlice";

const FloodedRice = () => {
	const { activeActivityId, project } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const { belongsToLuc } = useContext(ModuleContext)
	const id = moduleList.find((module) => module.id === FEModules.FloodedRice)?.uniqueId;

	const {
		data: floodedRiceData,
		isLoading,
		isError,
		error,
	} = useContextAwareGetFloodedRice(id ?? 0);
	const {
		data: seasonsData,
		isError: isSeasonsError,
		error: seasonsError,
	} = useContextAwareGetFloodedRiceMinorSeasons({ activityId: activeActivityId ?? 0 }, {
		refetchOnMountOrArgChange: true,
		skip: !activeActivityId,
	});

	const [
		updateFloodedRice,
		{ isLoading: isLoadingUpdate, isError: isUpdateError, error: updateError },
	] = useUpdateFloodedRiceMutation();
	const [
		updateSeason,
		{
			error: seasonError,
			isError: isUpdateSeasonError,
			isLoading: isUpdateSeasonsLoading,
		},
	] = useUpdateFloodedRiceMinorSeasonMutation();
	const { data: parentDefaults, isError: isDefaultsError, error: defaultsError } = useContextAwareGetFloodedRiceDefaults(
		floodedRiceData?.id ?? 0,
		{ skip: !floodedRiceData?.id },
	);
	const { data: cultivationDaysDefault } = useGetCultivationDaysDefaultQuery({ region: project?.country?.region?.id ?? 0 })
	const { data: cropYieldDefault } = useGetCropYieldDefaultQuery({ region: project?.country?.region?.id ?? 0, landUseType: 3 })
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });


	const { moduleSchema, t2Schema } =
		useMemo(() => getSchemas(), []);
	const [initValues, setInitValues] = useState<ModuleFull>({
		parent: initialValues,
		seasons: [],
	});

	const { tabsOpen, errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isUpdateSeasonsLoading, isLoadingUpdate],
		isMutateError: isUpdateSeasonError || isUpdateError,
		mutateError: seasonError ?? updateError,
	});
	const { errorMsg: blockingSeasonError } = useErrorMessage({
		isError: isSeasonsError,
		error: seasonsError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });
	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

	useEffect(() => {
		if (floodedRiceData) {
			const cultivationDaysDefaultsObj = cultivationDaysDefault ? {
				cultivation_period_t2_start_default: cultivationDaysDefault,
				cultivation_period_t2_w_default: cultivationDaysDefault,
				cultivation_period_t2_wo_default: cultivationDaysDefault,
			} : {};
			const cropYieldDefaultsObj = cropYieldDefault ? {
				crop_yield_t2_start_default: cropYieldDefault,
				crop_yield_t2_w_default: cropYieldDefault,
				crop_yield_t2_wo_default: cropYieldDefault,
			} : {};
			setInitValues((cur) => ({
				...cur,
				parent: { ...floodedRiceData, ...parentDefaults, ...cultivationDaysDefaultsObj, ...cropYieldDefaultsObj },
			}));
			processModuleThreads({
				parent: floodedRiceData,
				seasons: seasonsData ?? undefined
			})
		}
	}, [floodedRiceData, seasonsData, parentDefaults, processModuleThreads, cultivationDaysDefault, cropYieldDefault]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const parent = { ...values.parent };
		const parentPayload = {
			data: parent,
			activityId: activeActivityId ?? 0,
		};

		try {
			const promises = [];
			// Update seasons
			for (const season of values.seasons) {
				const seasonPayload = {
					body: season,
					activityId: activeActivityId ?? 0,
				};

				if (season.id) promises.push(updateSeason(seasonPayload));
			}

			await Promise.all(promises);
			await new Promise(resolve => setTimeout(resolve, 500));

			if (values.parent.id) await updateFloodedRice(parentPayload)
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema(belongsToLuc)}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<FloodedRiceTierOne
					title="project.flooded_rice"
					initValues={initValues}
					t2Schema={t2Schema}
					beModuleType={BEModulesClassNames.FloodedRice}
					moduleSchema={moduleSchema}
					footerError={errorMsg}
					blockingError={blockingSeasonError ?? blockingError}
					isError={isUpdateError || isUpdateSeasonError}
					setInitValues={setInitValues}
				/>
				<GenericTierTwo
					title="project.flooded_rice"
					t2Schema={t2Schema}
					tabsOpen={tabsOpen}
					warningMessage={defaultsErrorMsg}
				/>
			</>
		</Formik>
	);
};

export default FloodedRice;